import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  PhoneIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
  FlagIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { TemperaturePill, DispositionBadge, GoalOutcomeIcon, FollowUpStatusBadge } from '../../components/intelligence';
import api from '../../services/api';
import { contactsAPI } from '../../services/contactsAPI';
import { callsAPIv2, CallSummaryListItem } from '../../services/callsAPI';
import type { Contact } from '../../types/contact';
import PageLoader from '../../components/PageLoader';
import SendSMSModal from '../../components/SendSMSModal';
import SendEmailModal from '../../components/SendEmailModal';
import { useAuth } from '../../context/AuthContext';
import { useSmsEligibility } from '../../utils/entitlements';
import type { CallSummary } from '../RunProgressPage/runProgressTypes';

const contactDisplayName = (c: Contact | null): string => {
  if (!c) return '';
  const joined = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return joined || c.phone || '(unknown contact)';
};

const CallDetailPage: React.FC = () => {
  const { user } = useAuth();
  const { canSms } = useSmsEligibility(user);
  // LT-92: email no longer gated by per-user SMTP. Backend uses platform SendGrid + plan check.
  const canSendEmail = canSms;
  // Post-sequence-engine: the canonical route is /calls/:callSummaryId.
  // The legacy /runs/:runId/calls/:callSummaryId route still maps here for
  // back-compat, but runId is unused — GET /calls/{id} is workspace-scoped
  // (LT-253) and the contact is loaded via contactsAPI from summary.lead_id.
  const { callSummaryId } = useParams<{ callSummaryId: string }>();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<CallSummary | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [otherCalls, setOtherCalls] = useState<CallSummaryListItem[]>([]);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [smsToast, setSmsToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (!callSummaryId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const detailRes = await api.get(`/calls/${callSummaryId}`);
        if (cancelled) return;
        const found = detailRes.data as CallSummary;
        setSummary(found);

        const contactId = (found as CallSummary & { contact_id?: string }).contact_id || found.lead_id;
        if (contactId) {
          try {
            const c = await contactsAPI.get(contactId);
            if (!cancelled) setContact(c);
          } catch {
            if (!cancelled) setContact(null);
          }

          try {
            const hist = await callsAPIv2.list({ contact_id: contactId, limit: 20 });
            if (!cancelled) {
              setOtherCalls(hist.calls.filter((c) => c.id !== callSummaryId));
            }
          } catch {
            if (!cancelled) setOtherCalls([]);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load call details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [callSummaryId]);

  if (loading) return <PageLoader message="Loading call details..." />;
  if (error) return (
    <div className="p-6">
      <div className="bg-red-500/20 border border-red-400 text-red-100 px-4 py-3 rounded-lg">{error}</div>
    </div>
  );
  if (!summary) return (
    <div className="p-6">
      <div className="bg-yellow-500/20 border border-yellow-400 text-yellow-100 px-4 py-3 rounded-lg">Call not found</div>
    </div>
  );
  const displayName = contactDisplayName(contact);
  const contactPhone = contact?.phone || '';
  const contactEmail = (contact?.email || '').trim();

  const hasFollowUp = summary.follow_up_action?.action_required;
  const hasObjections = summary.objections && summary.objections.length > 0;
  const hasDecisionMaker = summary.decision_maker_confirmed !== undefined && summary.decision_maker_confirmed !== null;
  const hasContactInfo = summary.lead_provided_info && Object.values(summary.lead_provided_info).some((v) => v != null && v !== '');
  const hasGoalDetail = summary.goal_detail && summary.goal_detail.length > 0;
  // LT-626: campaign-declared capture fields extracted for this call.
  const capturedEntries = summary.captured_data
    ? Object.entries(summary.captured_data).filter(([, v]) => !isEmptyCaptured(v))
    : [];
  const hasCapturedData = capturedEntries.length > 0;
  const durationSeconds = summary.call_metadata?.duration_seconds;
  const canViewTranscripts = Boolean(user?.can_view_transcripts);


  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" />
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" />
      </div>

      <div className="relative px-3 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-6 max-w-[1400px] mx-auto">
        {/* ===== HEADLINE BAR ===== */}
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 p-3 sm:p-4 mb-4">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {contact ? (
              <Link
                to={`/contacts/${contact.id}`}
                className="text-lg sm:text-xl lg:text-2xl font-bold text-white hover:text-cyan-400 transition-colors"
              >
                {displayName}
              </Link>
            ) : (
              <span className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                {displayName || '(unknown contact)'}
              </span>
            )}
            <TemperaturePill temperature={summary.lead_temperature} />
            <DispositionBadge disposition={summary.disposition} />
            {summary.call_direction === 'inbound' && (
              <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium border bg-cyan-500/15 text-cyan-400 border-cyan-500/30">
                Inbound Call
              </span>
            )}
            {hasDecisionMaker && (
              summary.decision_maker_confirmed ? (
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium border bg-emerald-500/15 text-emerald-400 border-emerald-500/30">
                  <CheckCircleIcon className="h-3.5 w-3.5" /> Decision Maker
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium border bg-amber-500/15 text-amber-400 border-amber-500/30">
                  <QuestionMarkCircleIcon className="h-3.5 w-3.5" /> DM Unconfirmed
                </span>
              )
            )}
            <GoalOutcomeIcon outcome={summary.goal_outcome} showLabel />
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-400 flex-wrap">
            {contactPhone && (
              <span className="flex items-center gap-1">
                <PhoneIcon className="h-3.5 w-3.5" /> {contactPhone}
              </span>
            )}
            {summary.timestamp && (
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3.5 w-3.5" />
                {new Date(summary.timestamp).toLocaleString()}
              </span>
            )}
            {durationSeconds != null && (
              <span>{Math.floor(durationSeconds / 60)}m {Math.floor(durationSeconds % 60)}s</span>
            )}
            {canViewTranscripts && callSummaryId && (
              <Link
                to={`/calls/${callSummaryId}/transcript`}
                className="inline-flex items-center rounded-md border border-cyan-500/40 bg-cyan-500/10 px-2 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-500/20"
              >
                View Transcript
              </Link>
            )}
            {canSms && (
              <button
                type="button"
                onClick={() => setShowSmsModal(true)}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" /> Send SMS
              </button>
            )}
            {contactEmail && canSendEmail && (
              <button
                type="button"
                onClick={() => setShowEmailModal(true)}
                className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-xs font-medium text-sky-300 hover:bg-sky-500/20 transition-colors"
              >
                <EnvelopeIcon className="h-3.5 w-3.5" /> Send email
              </button>
            )}
          </div>
        </div>

        {/* ===== DETAIL SECTIONS - Two-column layout ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
          {/* Left column (60%) */}
          <div className="lg:col-span-3 space-y-4">
            {/* Summary (LLM analysis - the primary content) */}
            {summary.analysis_summary && (
              <Section title="Summary" icon={ChatBubbleLeftRightIcon}>
                <p className="text-sm text-slate-300 leading-relaxed">{summary.analysis_summary}</p>
              </Section>
            )}

            {/* Goal Breakdown */}
            {hasGoalDetail && (
              <Section title="Goal Breakdown" icon={CheckCircleIcon}>
                <div className="space-y-1.5">
                  {summary.goal_detail!.map((g, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm text-slate-200">{g.goal}</span>
                      <GoalOutcomeIcon outcome={g.achieved ? 'achieved' : 'no_progress'} />
                      <span className={`text-[10px] uppercase tracking-wider font-semibold ${g.achieved ? 'text-emerald-400' : 'text-red-400'}`}>
                        {g.achieved ? 'Met' : 'Not met'}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Why this rating - always visible, no collapse */}
            {summary.temperature_reason && (
              <Section title="Why this rating?" icon={ChatBubbleLeftRightIcon}>
                <p className="text-sm text-slate-300">{summary.temperature_reason}</p>
              </Section>
            )}

            {/* Captured data (LT-626) — campaign output_schema fields the
                analyser extracted for this call. */}
            {hasCapturedData && (
              <Section title="Captured data" icon={ClipboardDocumentListIcon}>
                <div className="space-y-3">
                  {capturedEntries.map(([key, value]) => (
                    <CapturedField key={key} fieldKey={key} value={value} />
                  ))}
                </div>
              </Section>
            )}
          </div>

          {/* Right column (40%) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Follow-up Required */}
            {hasFollowUp && (
              <div className="rounded-lg border-l-4 border-amber-500 bg-amber-500/10 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <FlagIcon className="h-4 w-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-amber-300">Follow-up Required</h3>
                  <FollowUpStatusBadge status={summary.follow_up_status} />
                  {summary.follow_up_action!.urgency && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-md font-medium ${
                      summary.follow_up_action!.urgency === 'high' ? 'bg-red-500/20 text-red-400' :
                      summary.follow_up_action!.urgency === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {summary.follow_up_action!.urgency}
                    </span>
                  )}
                </div>
                {summary.follow_up_action!.action_type && (
                  <p className="text-xs text-slate-400 mb-1">
                    Type: <span className="text-slate-300">{summary.follow_up_action!.action_type.replace(/_/g, ' ')}</span>
                  </p>
                )}
                {summary.follow_up_action!.description && (
                  <p className="text-sm text-slate-300">{summary.follow_up_action!.description}</p>
                )}
                {summary.follow_up_action!.requested_by && (
                  <p className="text-xs text-slate-400 mt-1">Requested by: <span className="text-slate-300">{summary.follow_up_action!.requested_by}</span></p>
                )}
                {summary.follow_up_action!.deadline_mentioned && (
                  <p className="text-xs text-slate-400 mt-1">Deadline: <span className="text-slate-300">{summary.follow_up_action!.deadline_mentioned}</span></p>
                )}
              </div>
            )}

            {/* Objections */}
            {hasObjections && (
              <Section title="Objections" icon={ChatBubbleLeftRightIcon}>
                <div className="space-y-1.5">
                  {summary.objections!.map((obj, i) => {
                    const text = typeof obj === 'string' ? obj : (obj as { objection?: string; response?: string }).objection || JSON.stringify(obj);
                    const response = typeof obj === 'object' && obj !== null ? (obj as { response?: string }).response : undefined;
                    return (
                      <div key={i} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                        <div>
                          <span className="text-sm text-slate-300">{text}</span>
                          {response && <p className="text-xs text-slate-400 mt-0.5">Response: {response}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {/* Decision Maker notes (only if there are notes beyond the badge) */}
            {hasDecisionMaker && summary.decision_maker_notes && (
              <Section title="Decision Maker Notes" icon={UserIcon}>
                <p className="text-sm text-slate-300">{summary.decision_maker_notes}</p>
              </Section>
            )}

            {/* Collected Info */}
            {hasContactInfo && (
              <Section title="Collected Info" icon={EnvelopeIcon}>
                <div className="space-y-2">
                  {summary.lead_provided_info!.email && <InfoRow label="Email" value={summary.lead_provided_info!.email} />}
                  {summary.lead_provided_info!.referral_name && <InfoRow label="Referral" value={summary.lead_provided_info!.referral_name} />}
                  {summary.lead_provided_info!.referral_phone && <InfoRow label="Referral Phone" value={summary.lead_provided_info!.referral_phone} />}
                  {summary.lead_provided_info!.preferred_callback_time && <InfoRow label="Callback" value={summary.lead_provided_info!.preferred_callback_time} />}
                  {summary.lead_provided_info!.other && <InfoRow label="Other" value={summary.lead_provided_info!.other} />}
                </div>
              </Section>
            )}
          </div>
        </div>

        {/* Other calls for this contact */}
        {otherCalls.length > 0 && (
          <div className="mt-6">
            <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 p-3 sm:p-4">
              <h3 className="text-sm font-semibold text-white mb-3">
                Other calls{displayName ? ` for ${displayName}` : ''}
              </h3>
              <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                {otherCalls.map((call) => {
                  const ts = call.timestamp ? new Date(call.timestamp) : null;
                  const hasIntelligence = !!(call.lead_temperature || call.disposition || call.analysis_summary);
                  return (
                    <button
                      key={call.id}
                      type="button"
                      onClick={() => navigate(`/calls/${call.id}`)}
                      className="flex-shrink-0 w-56 rounded-lg border border-slate-700/50 bg-slate-800/30 p-3 hover:border-slate-600 transition-colors text-left"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-400">
                          {ts ? `${ts.toLocaleDateString()} ${ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase()}` : ''}
                        </span>
                        {call.call_direction === 'inbound' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">
                            Inbound
                          </span>
                        )}
                      </div>
                      {hasIntelligence ? (
                        <>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <TemperaturePill temperature={call.lead_temperature} />
                            <DispositionBadge disposition={call.disposition} />
                          </div>
                          {call.analysis_summary && (
                            <p className="text-xs text-slate-300 line-clamp-2">{call.analysis_summary}</p>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span>{call.call_ended_reason || 'Completed'}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {smsToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-lg w-full mx-4">
          <div className={`p-3 rounded-lg flex items-center justify-between shadow-lg ${smsToast.type === 'success' ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
            <span className={`text-sm whitespace-pre-line ${smsToast.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{smsToast.message}</span>
            <button type="button" onClick={() => setSmsToast(null)} className="text-gray-400 hover:text-white ml-2">×</button>
          </div>
        </div>
      )}

      <SendSMSModal
        isOpen={showSmsModal}
        onClose={() => setShowSmsModal(false)}
        lead={contact && contactPhone ? { id: contact.id, name: displayName, phone: contactPhone } : null}
        setToast={setSmsToast}
      />
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        lead={
          contact && contactEmail
            ? { id: contact.id, name: displayName, email: contactEmail }
            : null
        }
        setToast={setSmsToast}
      />
    </div>
  );
};

function Section({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-700/50 bg-slate-800/30 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 w-24 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-300 break-all">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LT-626 — Captured data (campaign output_schema extraction)
// ---------------------------------------------------------------------------

/** snake_case key -> Title Case label. */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A captured value counts as "empty" (and is hidden) when it's null /
 * undefined / '' or an empty array. `false` and `0` are real values. */
function isEmptyCaptured(value: any): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Render a single scalar captured value as display text. */
function formatScalar(value: any): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function CapturedField({ fieldKey, value }: { fieldKey: string; value: any }) {
  const label = humanizeKey(fieldKey);
  // List-type value: array of row objects -> small table keyed by row keys.
  if (Array.isArray(value)) {
    const rows = value.filter((r) => r && typeof r === 'object');
    if (rows.length === 0) return null;
    const columns = Array.from(
      rows.reduce<Set<string>>((acc, row) => {
        Object.keys(row as Record<string, any>).forEach((k) => acc.add(k));
        return acc;
      }, new Set()),
    );
    return (
      <div>
        <div className="text-xs text-slate-400 mb-1">{label}</div>
        <div className="overflow-x-auto rounded-md border border-slate-700/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/40">
                {columns.map((col) => (
                  <th
                    key={col}
                    className="text-left px-2 py-1 text-[11px] font-medium text-slate-400 uppercase tracking-wider"
                  >
                    {humanizeKey(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-t border-slate-700/40">
                  {columns.map((col) => (
                    <td key={col} className="px-2 py-1 text-slate-300 align-top break-words">
                      {formatScalar((row as Record<string, any>)[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  // Scalar value: humanized label + value row.
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs text-slate-400 w-32 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-300 break-words">{formatScalar(value)}</span>
    </div>
  );
}

export default CallDetailPage;
